// AI-ASSISTED: generated with claude-code on 2026-08-28, reviewed by Yoann Hertienne
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

// Per-environment Azure Application Insights correlation (DPT-492 catalog wave,
// Phase 15). Two annotation families under the FR-011-allowlisted
// `azure.com/app-insights-` prefix:
//
//   azure.com/app-insights-resource-id-<env>      — full ARM resource id
//     (/subscriptions/…/providers/microsoft.insights/components/<name>). This is
//     what addresses the Azure portal blade, so it drives a real deep link.
//   azure.com/app-insights-application-id-<env>   — the API application-id GUID.
//     It cannot address the portal blade on its own, so when only this is
//     present we show it read-only (fallback) until a resource-id lands.
//
// Order follows the canonical Luminus DEV → TST → ACC → PRD progression; absent
// environments are simply not rendered.
const ENVIRONMENTS: ReadonlyArray<{ label: string; resourceIdAnnotation: string }> = [
  { label: 'DEV', resourceIdAnnotation: 'azure.com/app-insights-resource-id-dev' },
  { label: 'TST', resourceIdAnnotation: 'azure.com/app-insights-resource-id-tst' },
  { label: 'ACC', resourceIdAnnotation: 'azure.com/app-insights-resource-id-acc' },
  { label: 'PRD', resourceIdAnnotation: 'azure.com/app-insights-resource-id-prd' },
];

// An ARM resource id is an absolute path; the App Insights provider segment is
// stable. Guard against a mis-authored value (e.g. a bare GUID pasted into the
// resource-id key) so we never render a dead portal link.
const looksLikeArmResourceId = (value: string): boolean =>
  /^\/subscriptions\/[^/]+\/resourcegroups\/[^/]+\/providers\/microsoft\.insights\/components\/[^/]+/i.test(
    value,
  );

// Tenant-less portal deep link: `#resource<armId>/overview` resolves inside the
// signed-in user's directory (Luminus is single-tenant), so no tenant config is
// needed. encodeURI keeps the ARM path separators intact.
const portalUrlFor = (armResourceId: string): string =>
  `https://portal.azure.com/#resource${encodeURI(armResourceId)}/overview`;

type EnvRow = {
  label: string;
  resourceId?: string;
  applicationId?: string;
};

const AppInsightsCardBody = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  const rows: EnvRow[] = ENVIRONMENTS.map(env => {
    // The application-id key mirrors the resource-id key; deriving it keeps the
    // two families in lockstep without a second literal per environment.
    const applicationIdAnnotation = env.resourceIdAnnotation.replace(
      '-resource-id-',
      '-application-id-',
    );
    return {
      label: env.label,
      resourceId: annotations[env.resourceIdAnnotation]?.trim() || undefined,
      applicationId: annotations[applicationIdAnnotation]?.trim() || undefined,
    };
  }).filter(row => row.resourceId || row.applicationId);

  // Truthful "no data": hide the card entirely when no environment declares
  // either annotation (spec FR-004 / FR-008).
  if (rows.length === 0) return null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">Application Insights</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Per-environment Azure Application Insights resources for this component.
        </Typography>
        <Table size="small" aria-label="Application Insights environments">
          <TableBody>
            {rows.map(row => {
              const armId = row.resourceId;
              const canLink = Boolean(armId) && looksLikeArmResourceId(armId as string);
              return (
                <TableRow key={row.label}>
                  <TableCell
                    component="th"
                    scope="row"
                    // Env labels are short, fixed tokens (DEV/TST/ACC/PRD) — never
                    // let them wrap. `whiteSpace: nowrap` keeps the wider glyphs in
                    // ACC/PRD on one line; `width: '1%'` shrink-wraps the column so
                    // the value cell keeps the remaining width.
                    sx={{ width: '1%', whiteSpace: 'nowrap', fontWeight: 500 }}
                  >
                    {row.label}
                  </TableCell>
                  <TableCell>
                    {canLink ? (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        clickable
                        label="Open in Azure portal"
                        component="a"
                        href={portalUrlFor(armId as string)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open ${row.label} Application Insights in the Azure portal (opens in a new tab)`}
                      />
                    ) : (
                      // Fallback: only the application-id GUID is known, which
                      // can't address the portal blade — show it read-only.
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {row.applicationId ?? '—'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export const AppInsightsCard = () => (
  <TileBoundary tool="Application Insights">
    <AppInsightsCardBody />
  </TileBoundary>
);
